import React, { useState } from 'react';
import { confirmResetPassword } from 'aws-amplify/auth';
import PropTypes from 'prop-types';
import ErrorMessage from '../../ErrorMessage';
import { useForgotPasswordContext } from '../ForgotPasswordContext';
import { ShieldCheckIcon } from '../../../assets/icons';

function Step2({ nextStep }) {
  const { email, password, setPassword, userCode, setUserCode, error, setError, setIsPasswordChanged } = useForgotPasswordContext();
  const [confirmPassword, setConfirmPassword] = useState('');

  const resetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setError();
    try {
      await confirmResetPassword({
        username: email.trim().toLowerCase(),
        confirmationCode: userCode,
        newPassword: password,
      });
      setIsPasswordChanged(true);
      nextStep();
    } catch (err) {
      setError(err.message || err.name || 'Failed to reset password');
    }
  };

  return (
    <div className='px-6 py-9 sm:px-12 sm:py-11'>
      <div className='text-center'>
        <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#EBF4FB] text-[#0F2540]'>
          <ShieldCheckIcon className='h-7 w-7' strokeWidth='1.75' />
        </div>
        <h3 className='font-serif text-2xl font-normal leading-tight text-[#0B1929]'>Forgot Password</h3>
        <p className='mt-2 text-sm leading-6 text-[#7A9AB0]'>Enter the code we sent and choose a new password</p>
      </div>
      <form onSubmit={resetPassword} className='mt-7 space-y-4'>
        <div className='space-y-1.5'>
          <label htmlFor='verificationCode' className='block text-[11.5px] font-semibold text-[#7A9AB0]'>
            Verification Code
          </label>
          <input
            id='verificationCode'
            type='text'
            placeholder='Verification Code'
            value={userCode}
            onChange={(e) => setUserCode(e.target.value)}
            className='flex h-12 w-full rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white px-3.5 py-3 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10'
          />
        </div>
        <div className='space-y-1.5'>
          <label htmlFor='newPassword' className='block text-[11.5px] font-semibold text-[#7A9AB0]'>
            New Password
          </label>
          <input
            id='newPassword'
            type='password'
            placeholder='New Password'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className='flex h-12 w-full rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white px-3.5 py-3 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10'
          />
        </div>
        <div className='space-y-1.5'>
          <label htmlFor='confirmPassword' className='block text-[11.5px] font-semibold text-[#7A9AB0]'>
            Confirm New Password
          </label>
          <input
            id='confirmPassword'
            type='password'
            placeholder='Confirm New Password'
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className='flex h-12 w-full rounded-[10px] border-[1.5px] border-[#DDE6EF] bg-white px-3.5 py-3 text-sm text-[#0B1929] outline-none transition placeholder:text-[#7A9AB0]/70 focus:border-[#2E6BAD] focus:ring-4 focus:ring-[#2E6BAD]/10'
          />
        </div>
        <button
          className='inline-flex h-12 w-full items-center justify-center rounded-full bg-[#0B1929] px-7 text-sm font-semibold text-white transition hover:bg-[#0F2540] disabled:cursor-not-allowed disabled:bg-[#B8C9D9]'
          type='submit'
          disabled={userCode === '' || password === '' || confirmPassword === ''}
        >
          Continue
        </button>
        {error && <ErrorMessage error={error} className='form-error-display' />}
        <div className='flex items-center justify-center gap-2 pt-1'>
          <p className='text-sm text-[#7A9AB0]'>Remember your password?</p>
          <a href='/login' className='text-sm font-medium text-[#2E6BAD] hover:underline'>
            Sign in
          </a>
        </div>
      </form>
    </div>
  );
}

Step2.propTypes = {
  nextStep: PropTypes.func,
};

export default Step2;
